import { motion } from 'framer-motion';
import React from 'react';

const OurStory = () => {
  const timeline = [
    { 
      year: '2025', 
      month: 'January',
      title: 'Our Families Met', 
      description: 'Our families came together and decided to unite us in the beautiful bond of marriage, beginning a new journey blessed by our loved ones.',
    },
    { 
      year: '2025', 
      month: 'March',
      title: 'Getting to Know Each Other', 
      description: 'We spent time understanding each other\'s values, dreams, and aspirations, building a foundation of respect and companionship.',
    },
    { 
      year: '2025', 
      month: 'September',
      title: 'The Engagement', 
      description: 'Our families officially announced our engagement, marking the beginning of our wedding celebrations and preparations.',
    },
    { 
      year: '2026', 
      month: 'February',
      title: 'The Wedding', 
      description: 'With the blessings of our families and friends, we are ready to embark on this beautiful journey of marriage together.',
    }
  ];

  return (
    <section className="py-32 bg-white relative overflow-hidden">
      {/* Decorative Side Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 border-l border-t border-wedding-primary/10"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 border-r border-b border-wedding-primary/10"></div>

      <div className="container mx-auto px-4 max-w-5xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-24"
        >
          <motion.div 
            className="inline-block mb-6"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <div className="w-16 h-16 border border-wedding-primary/30 rounded-full flex items-center justify-center">
              <div className="w-10 h-10 border border-wedding-primary/50 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-wedding-primary rounded-full"></div>
              </div>
            </div>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-serif text-wedding-accent mb-6">
            Our Journey
          </h2>
          <p className="text-wedding-charcoal/60 max-w-xl mx-auto leading-relaxed">
            Every great love story has a beginning. Here's ours.
          </p>
        </motion.div>

        {/* Premium Timeline */}
        <div className="relative">
          {/* Vertical Line with Gradient */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-wedding-primary to-transparent hidden sm:block"></div>

          <div className="space-y-20">
            {timeline.map((item, index) => (
              <motion.div 
                key={index} 
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
              >
                {/* Content Side */}
                <div className={`flex-1 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16 md:text-left'} text-left pl-16 sm:pl-20`}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="inline-block w-full"
                  >
                    {/* Date Badge */}
                    <div className={`inline-flex items-center gap-2 mb-4 ${index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                      <div className="px-4 py-1 bg-wedding-blush text-wedding-accent text-xs uppercase tracking-widest font-medium">
                        {item.month}
                      </div>
                      <div className="px-4 py-1 bg-wedding-accent text-white text-xs uppercase tracking-widest font-medium">
                        {item.year}
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl md:text-3xl font-serif text-wedding-accent mb-4">
                      {item.title}
                    </h3>

                    {/* Description Card */}
                    <div className="relative group">
                      <div className="absolute inset-0 bg-wedding-primary/5 transform translate-x-1 translate-y-1 transition-transform group-hover:translate-x-2 group-hover:translate-y-2"></div>
                      <div className="relative bg-white border border-wedding-primary/20 p-6">
                        <p className="text-gray-600 leading-relaxed">
                          {item.description}
                        </p>
                        {/* Corner accent */}
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-wedding-primary/30"></div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Center Timeline Dot */}
                <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 flex items-center justify-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="relative"
                  >
                    <div className="w-4 h-4 bg-wedding-primary rounded-full relative z-10"></div>
                    <motion.div 
                      className="absolute inset-0 bg-wedding-primary rounded-full"
                      animate={{ scale: [1, 2, 1], opacity: [1, 0, 1] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    />
                  </motion.div>
                </div>

                {/* Empty Space for Alignment */}
                <div className="flex-1 hidden md:block"></div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom Decoration */}
        <motion.div
          className="mt-24 flex justify-center"
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-[1px] bg-wedding-primary"></div>
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <svg width="30" height="30" viewBox="0 0 100 100">
                <path 
                  d="M 50 20 Q 60 30, 50 40 Q 40 30, 50 20 M 50 40 Q 60 50, 50 60 Q 40 50, 50 40 M 50 60 Q 60 70, 50 80 Q 40 70, 50 60" 
                  fill="none" 
                  stroke="#C9A581" 
                  strokeWidth="1"
                />
              </svg>
            </motion.div>
            <div className="w-12 h-[1px] bg-wedding-primary"></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default OurStory;
